import { WorkfrontMappingKind } from '@atlas/types'
import { Button, DialogContent, DialogTitle, HelpText } from '@atlas/ui-primitives'
import { type FormEvent, useState } from 'react'
import type { AssetTypeRow, FieldRow, LookupRow, MappingRow, ValueRow } from '@/services/workfront-mappings.service'
import type { FieldErrors } from '../validation'
import type { DrawerContext } from './drawer-context'
import { FieldMappingForm } from './FieldMappingForm'
import { AssetTypeMappingForm, LookupRowForm, ValueMappingForm } from './RowForms'

export interface MappingDrawerProps {
	open: boolean
	kind: WorkfrontMappingKind
	title: string
	/** The row as it opens: a listed row, or a new one pre-filled (from a Fix, or Map this field). */
	initial: MappingRow
	/** For a value row: the Workfront labels mapped to the one value chosen. */
	labels?: string[]
	/** For a lookup row: the Workfront value's name. */
	sourceLabel?: string | null
	context: DrawerContext
	/** The last refusal's messages, by the field they concern; `_` for one that names none. */
	errors: FieldErrors
	saving?: boolean
	/** Opened from an error's Fix: the row may be published on its own. */
	canPublishNow?: boolean
	/** A listed row the draft may delete. */
	canDelete?: boolean
	onSave: (row: MappingRow, valueLabel?: string) => void
	onPublishNow?: (row: MappingRow) => void
	onDelete?: () => void
	onCancel: () => void
}

/** Whether the row has what its kind cannot be saved without. */
function isComplete(kind: WorkfrontMappingKind, row: MappingRow): boolean {
	if (kind === WorkfrontMappingKind.VALUE) return !!(row as ValueRow).valueId
	if (kind === WorkfrontMappingKind.ASSET_TYPE) {
		const assetType = row as AssetTypeRow
		return !!assetType.classificationValue.trim() && !!assetType.assetTypeCode
	}
	if (kind === WorkfrontMappingKind.LOOKUP) {
		const lookup = row as LookupRow
		return !!lookup.sourceValue.trim() && !!lookup.targetValue.trim()
	}
	return !!(row as FieldRow).targetPath
}

function KindForm({
	kind,
	row,
	setRow,
	labels,
	sourceLabel,
	errors,
	context,
}: Pick<MappingDrawerProps, 'kind' | 'labels' | 'sourceLabel' | 'errors' | 'context'> & {
	row: MappingRow
	setRow: (row: MappingRow) => void
}) {
	const shared = { errors, context }
	switch (kind) {
		case WorkfrontMappingKind.FIELD:
			return <FieldMappingForm row={row as FieldRow} onChange={(patch) => setRow({ ...row, ...patch })} {...shared} />
		case WorkfrontMappingKind.VALUE: {
			const value = row as ValueRow
			return (
				<ValueMappingForm
					row={value}
					labels={labels ?? [value.label]}
					onChange={(patch) => setRow({ ...value, ...patch })}
					{...shared}
				/>
			)
		}
		case WorkfrontMappingKind.ASSET_TYPE:
			return (
				<AssetTypeMappingForm
					row={row as AssetTypeRow}
					onChange={(patch) => setRow({ ...row, ...patch })}
					{...shared}
				/>
			)
		default:
			return (
				<LookupRowForm
					row={row as LookupRow}
					sourceLabel={sourceLabel}
					onChange={(patch) => setRow({ ...row, ...patch })}
					{...shared}
				/>
			)
	}
}

/**
 * The edit drawer of the Fields, Values, Asset types and Lookups tabs: a side panel
 * holding one row's form. Saving puts the row in the draft; a refusal's message
 * shows under the field it concerns. Keyboard-operable: focus stays inside, Esc closes.
 */
export function MappingDrawer(props: MappingDrawerProps) {
	const { open, kind, title, initial, context, errors, saving, canPublishNow, canDelete } = props
	const [row, setRow] = useState<MappingRow>(initial)
	if (!open) return null

	const complete = isComplete(kind, row) && !saving
	const submit = (event: FormEvent) => {
		event.preventDefault()
		if (!complete) return
		const valueLabel =
			kind === WorkfrontMappingKind.VALUE
				? context.axisValues.find((value) => value.valueId === (row as ValueRow).valueId)?.label
				: undefined
		props.onSave(row, valueLabel)
	}

	return (
		<DialogContent
			size="lg"
			isOpen={open}
			isDismissable
			onOpenChange={(next) => (next ? undefined : props.onCancel())}
			overlayClassName="items-stretch justify-end p-0"
			className="h-full overflow-y-auto rounded-none"
		>
			<form onSubmit={submit} className="space-y-4">
				<DialogTitle>{title}</DialogTitle>
				{errors._ ? <HelpText variant="error">{errors._}</HelpText> : null}
				<KindForm {...props} row={row} setRow={setRow} />
				<div className="flex flex-wrap justify-end gap-2 pt-2">
					{canDelete && props.onDelete ? (
						<Button variant="ghost" isDisabled={saving} onPress={props.onDelete}>
							Delete in draft
						</Button>
					) : null}
					<Button variant="outline" onPress={props.onCancel}>
						Cancel
					</Button>
					{canPublishNow && props.onPublishNow ? (
						<Button variant="outline" isDisabled={!complete} onPress={() => props.onPublishNow?.(row)}>
							Publish now
						</Button>
					) : null}
					<Button type="submit" isDisabled={!complete}>
						{saving ? 'Saving…' : 'Save to draft'}
					</Button>
				</div>
			</form>
		</DialogContent>
	)
}
